import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import "./ChatPopup.css";
import ChatBot from "../ChatBot/ChatBot";
import { trackChipUx } from "../ChatBot/chipAnalytics";

// Docked right-side panel, position:"fixed" to the viewport (see ChatPopup.css) —
// always spans from just below the header to the bottom of the viewport,
// independent of page scroll/content height. Resizable by width only via the left
// edge. Sizing mirrors CHIP's structural reference: 380px default, 340-800px range.
// MIN_WIDTH raised from 340 so the composer placeholder ("Ask about orders, parts,
// documents, or your cart") always fits on one line — this also makes any narrower
// width already cached in a browser's localStorage from before this change snap up
// to the new minimum automatically, since the clamp below re-runs on every load.
const MIN_WIDTH = 460;
const MAX_WIDTH = 800;
// Default is 25% of the current viewport width (clamped to MIN/MAX_WIDTH above) rather
// than a fixed pixel value, so it scales with screen size. On narrower/typical desktop
// widths (~1200-1800px), 25% falls below MIN_WIDTH and gets clamped up to 460 — that
// floor exists so the composer placeholder always fits on one line, so it still wins
// over the percentage on those screens.
const getDefaultWidth = () => {
	if (typeof window === "undefined") return MIN_WIDTH;
	return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth * 0.25));
};

export default function ChatPopup({ open, onClose }) {
	const [panelWidth, setPanelWidth] = useState(() => {
		try {
			const raw = Number(localStorage.getItem("chip_chat_width"));
			if (Number.isFinite(raw)) return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, raw));
		} catch (_error) {}
		return getDefaultWidth();
	});
	const [isResizing, setIsResizing] = useState(false);
	const [fullPage, setFullPage] = useState(false);
	const resizeRef = useRef({ startX: 0, startWidth: getDefaultWidth() });

	// Analytics: the panel is controlled from outside (Header toggle flips `open`),
	// so open/close is observed here rather than in a handler. The ref skips the
	// initial mount so a page load with the panel closed emits nothing.
	const prevOpenRef = useRef(null);
	useEffect(() => {
		if (prevOpenRef.current === null) {
			prevOpenRef.current = open;
			if (open) trackChipUx("chip_opened", { screen_path: window.location.pathname });
			return;
		}
		if (prevOpenRef.current !== open) {
			prevOpenRef.current = open;
			trackChipUx(open ? "chip_opened" : "chip_closed", { screen_path: window.location.pathname });
		}
	}, [open]);

	const handleResizeStart = (event) => {
		event.preventDefault();
		resizeRef.current = { startX: event.clientX, startWidth: panelWidth };
		setIsResizing(true);
	};

	useEffect(() => {
		if (!isResizing) return undefined;

		const handleMouseMove = (event) => {
			const delta = resizeRef.current.startX - event.clientX;
			const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeRef.current.startWidth + delta));
			setPanelWidth(newWidth);
		};

		const handleMouseUp = () => {
			setIsResizing(false);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
			setPanelWidth((current) => {
				try {
					localStorage.setItem("chip_chat_width", String(current));
				} catch (_error) {}
				return current;
			});
		};

		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";
		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		};
	}, [isResizing]);

	// Publish the panel's effective width to a document-root CSS var so Layout2's content
	// column can reserve it via margin-right (page reflows/shrinks as the panel opens/closes
	// and while it's dragged to resize). 0 when closed. Cleared on unmount so a page rendered
	// without the panel (routes outside Layout2) isn't left with a phantom right margin.
	// In full-page mode the panel covers the whole viewport, so the page content shouldn't
	// reserve any right-margin — publish 0 (same as closed). Docked → the panel width.
	useEffect(() => {
		const w = open && !fullPage ? panelWidth : 0;
		document.documentElement.style.setProperty("--chip-panel-w", `${w}px`);
		return () => {
			document.documentElement.style.setProperty("--chip-panel-w", "0px");
		};
	}, [open, panelWidth, fullPage]);

	// No `if (!open) return null` — <ChatBot> stays mounted at all times (just
	// slid off-screen via transform below) so its in-memory conversationHistory
	// survives being hidden. The header's existing chip toggle button (Header.js)
	// already flips this same `open` value, so reopening shows the SAME
	// conversation instead of a blank one. The only way to actually reset it is
	// CHIP's own Clear/Delete button inside ChatBot's header — "closing" this
	// panel no longer destroys anything.
	//
	// Width stays constant (not conditionally 0) — visibility/open-close is
	// entirely driven by the chip-panel-hidden class's transform:translateX in
	// ChatPopup.css, which is what makes the open/close a slide instead of a
	// grow/shrink. The resize handle and ChatBot are always rendered/flex — while
	// closed, chip-panel-hidden's pointer-events:none (inherited by all children)
	// already makes them non-interactive, so no conditional display needed here.
	return (
		<Box
			className={`chip-panel ${open ? "" : "chip-panel-hidden"} ${fullPage ? "chip-panel-full" : ""}`}
			sx={fullPage ? undefined : { width: panelWidth, minWidth: MIN_WIDTH, maxWidth: MAX_WIDTH }}
		>
			{!fullPage && (
				<Box
					className={`chip-panel-resize-handle ${isResizing ? "resizing" : ""}`}
					onMouseDown={handleResizeStart}
				/>
			)}
			<Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
				<ChatBot
					onClose={() => {
						setFullPage(false);
						onClose?.();
					}}
					fullPage={fullPage}
					onToggleFullPage={() => setFullPage((v) => !v)}
				/>
			</Box>
		</Box>
	);
}
